import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { Box, Typography, Fade } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import InfoIcon from "@mui/icons-material/Info";

const VARIANTS = {
  success: { color: "#16A34A", icon: CheckIcon },
  error: { color: "#DC2626", icon: CloseIcon },
  warning: { color: "#D97706", icon: PriorityHighIcon },
  info: { color: "#2563EB", icon: InfoIcon },
};

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const showToast = useCallback((message, severity = "success", duration = 4000) => {
    if (!message) return;
    clearTimeout(timerRef.current);
    setToast({ message, severity, key: Date.now() });
    timerRef.current = setTimeout(() => setToast(null), duration);
  }, []);

  const hideToast = useCallback(() => {
    clearTimeout(timerRef.current);
    setToast(null);
  }, []);

  const meta = VARIANTS[toast?.severity] || VARIANTS.success;
  const Icon = meta.icon;

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Fade in={!!toast} unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "#fff",
            borderRadius: 3,
            borderTop: `4px solid ${meta.color}`,
            boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
            py: 1.5,
            pl: 1.5,
            pr: 3,
            minWidth: 280,
            maxWidth: "90vw",
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: meta.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#1a1a1a", wordBreak: "break-word" }}
          >
            {toast?.message}
          </Typography>
        </Box>
      </Fade>
    </ToastContext.Provider>
  );
}

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
};